import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Grid, Box, TextField, Autocomplete, MenuItem } from "@mui/material";
import MDButton from "shared/components/MDButton";
import MDTypography from "shared/components/MDTypography";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { validarRangoFechas } from "../../utils/validarRangoFechas";

const fieldSx = (theme) => ({
  "& .MuiInputBase-root": {
    height: 56,
    fontSize: 16,
    borderRadius: 2,
    bgcolor: "background.paper",
    color: "text.primary",
  },
  "& .MuiInputLabel-root": {
    fontSize: 16,
    fontWeight: 600,
    color: "text.secondary",
    "&.Mui-focused": { color: "info.main" },
    "&.Mui-error": { color: "error.main" },
  },
  "& .MuiInputLabel-root.Mui-disabled": { color: "text.secondary" },
  "& .MuiFormHelperText-root": { fontSize: 12, lineHeight: 1.5, mt: 0.75 },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.mode === "dark" ? "#607287" : "#94a3b8",
  },
  "& .MuiOutlinedInput-root:not(.Mui-disabled):not(.Mui-error):hover .MuiOutlinedInput-notchedOutline":
    { borderColor: "text.primary" },
  "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
    borderColor: "info.main",
    borderWidth: 2,
  },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "error.main",
  },
  "& .MuiInputBase-root.Mui-disabled": { bgcolor: "action.disabledBackground" },
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: theme.palette.text.secondary },
});
const buttonSx = {
  "&&": { height: 56, fontSize: 14, fontWeight: 700, textTransform: "none", borderRadius: 2 },
};

const FilterSection = ({
  concesiones = [],
  peajes = [],
  selectedConcesion,
  selectedPeaje,
  selectedFechaInicio,
  selectedFechaFin,
  setSelectedConcesion,
  setSelectedPeaje,
  setSelectedFechaInicio,
  setSelectedFechaFin,
  showResults,
  handleBuscar,
  handleLimpiarTodo,
  loading = false,
  loadingPeajes = false,
}) => {
  // ------------------------------------------------------------
  // ESTADOS DE VALIDACIÓN
  // ------------------------------------------------------------
  const [errors, setErrors] = useState({
    fechaInicio: "",
    fechaFin: "",
  });

  // ------------------------------------------------------------
  // VALIDACIONES
  // ------------------------------------------------------------
  const validar = () => {
    const newErrors = validarRangoFechas(selectedFechaInicio, selectedFechaFin);

    setErrors(newErrors);
    return !newErrors.fechaInicio && !newErrors.fechaFin;
  };

  // Revalidar cada vez que cambian las fechas
  useEffect(() => {
    validar();
  }, [selectedFechaInicio, selectedFechaFin]);

  // Botón habilitado solo si pasa todas las validaciones
  const formValido =
    !!selectedConcesion &&
    !!selectedFechaInicio &&
    !!selectedFechaFin &&
    !errors.fechaInicio &&
    !errors.fechaFin;

  // ------------------------------------------------------------
  // HANDLERS
  // ------------------------------------------------------------
  const onConcesionChange = (event, newValue) => {
    setSelectedConcesion(newValue ? newValue.nombre : "");
  };

  const onPeajeChange = (e) => setSelectedPeaje(e.target.value);

  const onFechaInicioChange = (e) => {
    const value = e.target.value;
    if (value) {
      const date = dayjs(value, "YYYY-MM-DD");
      if (date.isValid()) {
        setSelectedFechaInicio(date.toDate());
        if (selectedFechaFin && dayjs(selectedFechaFin).isBefore(date, "day")) {
          setSelectedFechaFin(null);
        }
      }
    } else {
      setSelectedFechaInicio(null);
    }
  };

  const onFechaFinChange = (e) => {
    const value = e.target.value;
    if (value) {
      const date = dayjs(value, "YYYY-MM-DD");
      if (date.isValid()) {
        setSelectedFechaFin(date.toDate());
      }
    } else {
      setSelectedFechaFin(null);
    }
  };

  const onBuscarClick = () => {
    if (loading || !formValido || loadingPeajes || !validar()) return;
    handleBuscar();
  };

  const formatFechaInput = (date) => (date ? dayjs(date).format("YYYY-MM-DD") : "");
  return (
    <Box
      component="fieldset"
      disabled={loading}
      sx={{ border: 0, m: 0, p: { xs: 2, md: 3 }, borderBottom: "1px solid #e5e7eb", minWidth: 0 }}
    >
      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} sm={6} xxl={3}>
          <Autocomplete
            disabled={loading}
            options={concesiones}
            getOptionLabel={(option) => option.nombre || ""}
            value={concesiones.find((c) => c.nombre === selectedConcesion) || null}
            onChange={onConcesionChange}
            isOptionEqualToValue={(option, value) => option.nombre === value?.nombre}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Concesión"
                required
                size="medium"
                placeholder="Buscar concesión"
                sx={fieldSx}
              />
            )}
            noOptionsText="Sin resultados"
          />
        </Grid>
        <Grid item xs={12} sm={6} xxl={3}>
          <TextField
            select
            fullWidth
            size="medium"
            label="Peaje"
            value={selectedPeaje || ""}
            onChange={onPeajeChange}
            disabled={loading || !selectedConcesion || loadingPeajes}
            sx={fieldSx}
            helperText={loadingPeajes ? "Cargando peajes…" : "Opcional · Todos los peajes"}
          >
            <MenuItem value="">Todos los peajes</MenuItem>
            {peajes.map((p, idx) => (
              <MenuItem key={idx} value={p.peajeLimpio || p.peaje}>
                {p.peaje}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} xxl={2}>
          <TextField
            fullWidth
            required
            size="medium"
            label="Fecha inicio"
            type="date"
            value={formatFechaInput(selectedFechaInicio)}
            onChange={onFechaInicioChange}
            disabled={loading || !selectedConcesion}
            error={!!errors.fechaInicio}
            helperText={errors.fechaInicio || "Inicio del período"}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6} xxl={2}>
          <TextField
            fullWidth
            required
            size="medium"
            label="Fecha fin"
            type="date"
            value={formatFechaInput(selectedFechaFin)}
            onChange={onFechaFinChange}
            disabled={loading || !selectedFechaInicio}
            error={!!errors.fechaFin}
            helperText={errors.fechaFin || "Fin del período"}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: formatFechaInput(selectedFechaInicio) }}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} xxl={2}>
          <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
            <MDButton
              variant="contained"
              color="info"
              onClick={onBuscarClick}
              disabled={loading || !formValido || loadingPeajes}
              startIcon={<SearchIcon />}
              sx={{ ...buttonSx, flex: { xs: 1, sm: "0 0 auto" }, minWidth: 112 }}
            >
              Buscar
            </MDButton>
            <MDButton
              variant="text"
              color="secondary"
              onClick={handleLimpiarTodo}
              disabled={
                loading ||
                (!selectedConcesion && !selectedFechaInicio && !selectedFechaFin && !showResults)
              }
              sx={{ ...buttonSx, minWidth: 88 }}
            >
              Limpiar
            </MDButton>
          </Box>
        </Grid>
      </Grid>
      {!selectedConcesion && (
        <MDTypography variant="caption" color="text">
          Selecciona una concesión y el período que deseas consultar.
        </MDTypography>
      )}
    </Box>
  );
};

FilterSection.propTypes = {
  concesiones: PropTypes.array,
  peajes: PropTypes.array,
  selectedConcesion: PropTypes.string,
  selectedPeaje: PropTypes.string,
  selectedFechaInicio: PropTypes.object,
  selectedFechaFin: PropTypes.object,
  setSelectedConcesion: PropTypes.func.isRequired,
  setSelectedPeaje: PropTypes.func.isRequired,
  setSelectedFechaInicio: PropTypes.func.isRequired,
  setSelectedFechaFin: PropTypes.func.isRequired,
  showResults: PropTypes.bool.isRequired,
  handleBuscar: PropTypes.func.isRequired,
  handleLimpiarTodo: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadingPeajes: PropTypes.bool,
};

FilterSection.defaultProps = {
  concesiones: [],
  peajes: [],
  selectedConcesion: "",
  selectedPeaje: "",
  selectedFechaInicio: null,
  selectedFechaFin: null,
  loadingPeajes: false,
};

export default FilterSection;
